import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { Page } from "@/components/layout/page";
import {
  EntityNotFound,
  EntityErrorState,
} from "@/components/layout/entity-state";
import { Skeleton } from "@/components/ui/skeleton";
import { isValidUuid } from "@/lib/utils";

// Workspace Subcomponents & Hooks
import { useWalkthroughWorkspace } from "@/components/walkthrough/workspace/use-walkthrough-workspace";
import { WorkspaceHeaderActions } from "@/components/walkthrough/workspace/workspace-header-actions";
import { WorkspaceEyebrow } from "@/components/walkthrough/workspace/workspace-eyebrow";
import { WorkspaceWorkbench } from "@/components/walkthrough/workspace/workspace-workbench";
import { WorkspaceOverlays } from "@/components/walkthrough/workspace/workspace-overlays";
import { WorkspaceHumanTestBanner } from "@/components/walkthrough/workspace/workspace-human-test-banner";

export function WalkthroughWorkspacePage() {
  const { id: projectId, walkthroughId } = useParams<{
    id: string;
    walkthroughId: string;
  }>();
  const location = useLocation();
  const fromState = location.state as
    | { from?: string; label?: string }
    | undefined;
  const back = useMemo(
    () => ({
      to: fromState?.from ?? `/projects/${projectId}/walkthroughs`,
      label: fromState?.label ?? "Walkthroughs",
    }),
    [fromState, projectId],
  );

  const isValidId = isValidUuid(walkthroughId);
  const navigate = useNavigate();
  const {
    data,
    searchParams,
    selection,
    canEdit,
    hasLinkedRepo,
    isTrackerConfigured,
    activeProvider,
    exportConfig,
    mutations,
    share,
    copyPrompt,
    workRequests,
    handlers,
    zen,
  } = useWalkthroughWorkspace({ projectId, walkthroughId, isValidId });

  const {
    activeTab,
    setActiveTab,
    statusFilter,
    setStatusFilter,
    kindFilter,
    setKindFilter,
    severityFilter,
    setSeverityFilter,
    selectBaseRun,
    setReportOpen,
    stepNumber: stepNumberFromUrl,
    reportOpen,
  } = searchParams;

  const {
    walkthrough,
    isLoading,
    error,
    refetch,
    scenarioRuns,
    runsChronological,
    runNumber,
    walkthroughSteps,
    allFindings,
    filteredFindings,
    activeFinding,
    activeStep,
    problemsCount,
    helpsCount,
    openCount,
    guidanceRules,
    findingTitles,
    htmlReport,
    whatWorked,
    openQuestions,
    verdicts,
    activeBaseRunId,
    defaultBaseRunId,
  } = data;

  const {
    selectedIds,
    focusedIndex,
    toggle: toggleSelect,
    toggleAll: toggleSelectAll,
    clear: clearSelection,
  } = selection;

  const { handleOpenFinding, handleCloseDetail, handleOpenZenMode } = handlers;
  const { zenModal, setZenModal, zenFindings } = zen;

  if (!projectId || !walkthroughId || !isValidId) {
    return (
      <EntityNotFound
        title="Walkthrough not found"
        entityName="walkthrough"
        backLink={{ to: back.to, label: `Back to ${back.label}` }}
      />
    );
  }
  if (isLoading) {
    return (
      <Page title="Walkthrough" back={back}>
        <div className="space-y-6" aria-busy="true">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </Page>
    );
  }
  if (error || !walkthrough) {
    return error?.data?.code === "NOT_FOUND" || !walkthrough ? (
      <EntityNotFound
        title="Walkthrough not found"
        entityName="walkthrough"
        backLink={{ to: back.to, label: `Back to ${back.label}` }}
      />
    ) : (
      <EntityErrorState
        title="Failed to load walkthrough"
        onRetry={() => refetch()}
        backLink={{ to: back.to, label: `Back to ${back.label}` }}
      />
    );
  }

  const isRunning =
    walkthrough.status === "running" || walkthrough.status === "pending";
  const isFailed = walkthrough.status === "failed";

  const allFilteredSelected =
    filteredFindings.length > 0 &&
    filteredFindings.every((f) => selectedIds.has(f.id));
  const someFilteredSelected =
    filteredFindings.some((f) => selectedIds.has(f.id)) && !allFilteredSelected;

  const selectedCanonicalIds = allFindings
    .filter((f) => selectedIds.has(f.id) && f.canonicalFindingId)
    .map((f) => f.canonicalFindingId as string);

  return (
    <Page
      title={walkthrough.scenario?.name ?? "Walkthrough"}
      back={back}
      eyebrow={
        <WorkspaceEyebrow
          scenarioRuns={scenarioRuns}
          runsChronological={runsChronological}
          walkthroughId={walkthroughId}
          runNumber={runNumber}
          projectId={projectId}
          status={walkthrough.status}
          completedAt={walkthrough.completedAt}
        />
      }
      actions={
        <WorkspaceHeaderActions
          canEdit={canEdit}
          isRerunPending={mutations.rerunMutation.isPending}
          onRerun={() => {
            if (walkthrough?.scenarioId && walkthrough?.targetUrl) {
              mutations.rerunMutation.mutate({
                scenarioId: walkthrough.scenarioId,
                targetUrl: walkthrough.targetUrl,
              } as any);
            } else if (walkthrough?.scenarioId) {
              navigate(
                `/projects/${projectId}/scenarios/${walkthrough.scenarioId}`,
              );
            } else {
              navigate(`/projects/${projectId}/scenarios`);
            }
          }}
          onDownloadPdf={share.handleDownloadPdf}
          onDownloadHtml={share.handleDownloadHtml}
          onSharePublicLink={share.handleSharePublicLink}
          onRevokeShareLink={share.handleRevokeShareLink}
          onCopyLink={share.handleCopyLink}
          onOpenReportDialog={() => setReportOpen(true)}
          onCopyPrompt={() => copyPrompt.copy()}
          isCopyingPrompt={copyPrompt.isCopying}
          canCopyPrompt={allFindings.length > 0}
          hasHtmlReport={!!htmlReport?.content}
          isSharing={share.isSharing}
          shareInfo={share.shareInfo}
        />
      }
    >
      <WorkspaceHumanTestBanner
        walkthrough={walkthrough}
        verdicts={verdicts}
        scenarioRuns={scenarioRuns}
        findingTitles={findingTitles}
        projectId={projectId}
        onOpenFinding={handleOpenFinding}
      />

      <WorkspaceWorkbench
        walkthrough={walkthrough}
        walkthroughSteps={walkthroughSteps}
        allFindings={allFindings}
        filteredFindings={filteredFindings}
        problemsCount={problemsCount}
        helpsCount={helpsCount}
        openCount={openCount}
        verdicts={verdicts}
        findingTitles={findingTitles}
        kindFilter={kindFilter}
        onKindFilterChange={setKindFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        severityFilter={severityFilter}
        onSeverityFilterChange={setSeverityFilter}
        selectedIds={selectedIds}
        focusedIndex={focusedIndex}
        allFilteredSelected={allFilteredSelected}
        someFilteredSelected={someFilteredSelected}
        onToggleSelectAll={() =>
          toggleSelectAll(filteredFindings.map((f) => f.id))
        }
        onToggleSelect={toggleSelect}
        onOpenFinding={handleOpenFinding}
        onOpenZenMode={handleOpenZenMode}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        stepNumberFromUrl={stepNumberFromUrl}
        scenarioRuns={scenarioRuns}
        runsChronological={runsChronological}
        runNumber={runNumber}
        defaultBaseRunId={defaultBaseRunId}
        activeBaseRunId={activeBaseRunId}
        selectBaseRun={selectBaseRun}
        projectId={projectId}
        canEdit={canEdit}
        hasLinkedRepo={hasLinkedRepo}
        isTrackerConfigured={isTrackerConfigured}
        guidanceRules={guidanceRules}
        htmlReport={htmlReport}
        setReportOpen={setReportOpen}
        mutations={mutations}
        isRunning={isRunning}
        isFailed={isFailed}
      />

      <WorkspaceOverlays
        walkthrough={walkthrough}
        allFindings={allFindings}
        walkthroughSteps={walkthroughSteps}
        runNumber={runNumber}
        verdicts={verdicts}
        whatWorked={whatWorked}
        openQuestions={openQuestions}
        activeFinding={activeFinding}
        activeStep={activeStep}
        projectId={projectId}
        canEdit={canEdit}
        hasLinkedRepo={hasLinkedRepo}
        isTrackerConfigured={isTrackerConfigured}
        activeProvider={activeProvider}
        exportConfig={exportConfig}
        mutations={mutations}
        share={share}
        reportOpen={reportOpen}
        setReportOpen={setReportOpen}
        htmlReport={htmlReport}
        selectedIds={selectedIds}
        selectedCanonicalIds={selectedCanonicalIds}
        filteredFindings={filteredFindings}
        focusedIndex={focusedIndex}
        kindFilter={kindFilter}
        zenModal={zenModal}
        setZenModal={setZenModal}
        zenFindings={zenFindings}
        workRequests={workRequests}
        clearSelection={clearSelection}
        handleCloseDetail={handleCloseDetail}
        setActiveTab={setActiveTab}
        handleOpenZenMode={handleOpenZenMode}
      />
    </Page>
  );
}
